/**
 * API Client Base
 * Cliente HTTP base con interceptores para autenticación y manejo de errores
 */

import API_CONFIG from '@/config/api.config';
import { clearSessionData } from '@/utils/tokenValidation';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface RequestConfig extends RequestInit {
  timeout?: number;
  requiresAuth?: boolean;
}

class ApiClient {
  private baseURL: string;
  private timeout: number;

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  /**
   * Obtiene el token de autenticación del usuario actual
   * Usa el helper centralizado para mantener consistencia
   */
  private async getAuthToken(): Promise<string | null> {
    const { getAuthToken } = await import('@/utils/authToken');
    return getAuthToken(true);
  }

  /**
   * Construye los headers de la petición
   */
  private async buildHeaders(requiresAuth: boolean = true): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (requiresAuth) {
      const token = await this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Realiza una petición HTTP con timeout
   */
  private async fetchWithTimeout(
    url: string,
    config: RequestInit,
    timeout: number
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort(new DOMException(`Request timed out after ${timeout}ms`, 'TimeoutError'));
    }, timeout);

    try {
      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Método principal para realizar peticiones
   */
  private async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      timeout = this.timeout,
      requiresAuth = true,
      headers: customHeaders,
      ...restConfig
    } = config;

    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.buildHeaders(requiresAuth);

    try {
      const response = await this.fetchWithTimeout(
        url,
        {
          ...restConfig,
          headers: {
            ...headers,
            ...customHeaders,
          },
        },
        timeout
      );

      // Parsear respuesta
      let data;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Manejar errores HTTP (202 Accepted también es válido para operaciones asíncronas)
      if (!response.ok && response.status !== 202) {
        // Token expirado o inválido: borrar y redirigir a login para obtener uno nuevo
        if (response.status === 401 && typeof localStorage !== 'undefined') {
          clearSessionData();
          // Solo redirigir si no estamos ya en la ruta raíz
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
        }
        // Si el servidor no está disponible (503), no limpiar el token pero mostrar error
        // El usuario puede seguir intentando cuando el backend esté disponible
        return {
          success: false,
          error: data.detail || data.message || `HTTP Error ${response.status}`,
          data: data,
        };
      }

      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      console.error('API Request Error:', error);

      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        return {
          success: false,
          error: 'Request timeout',
        };
      }

      return {
        success: false,
        error: error.message || 'Network error',
      };
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'GET',
    });
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    body?: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    body?: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(
    endpoint: string,
    body?: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'DELETE',
    });
  }

  /**
   * Upload file (multipart/form-data)
   */
  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalFields?: Record<string, string>,
    config?: Omit<RequestConfig, 'headers'>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    // Agregar campos adicionales si existen
    if (additionalFields) {
      Object.entries(additionalFields).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const token = await this.getAuthToken();
    const headers: HeadersInit = {};

    if (config?.requiresAuth !== false && token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${this.baseURL}${endpoint}`;

    try {
      const response = await this.fetchWithTimeout(
        url,
        {
          method: 'POST',
          body: formData,
          headers,
        },
        config?.timeout || this.timeout
      );

      const data = await response.json();

      // 202 Accepted también es válido para operaciones asíncronas (como upload de JD)
      if (!response.ok && response.status !== 202) {
        return {
          success: false,
          error: data.detail || data.message || `HTTP Error ${response.status}`,
          data: data,
        };
      }

      return {
        success: true,
        data: data,
      };
    } catch (error: any) {
      console.error('Upload Error:', error);
      
      // Detectar timeout (AbortController sin razón = AbortError; con DOMException = TimeoutError)
      if (
        error.name === 'AbortError' ||
        error.name === 'TimeoutError' ||
        error.message?.includes('timeout')
      ) {
        return {
          success: false,
          error: 'Request timeout - El proceso está tomando más tiempo del esperado. Por favor, verifica en el panel de vacantes si la vacante se creó correctamente.',
        };
      }
      
      return {
        success: false,
        error: error.message || 'Upload failed',
      };
    }
  }

  /**
   * Upload to signed URL (para GCS)
   */
  async uploadToSignedUrl(
    signedUrl: string,
    file: File,
    contentType: string
  ): Promise<boolean> {
    try {
      const response = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': contentType,
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Upload to signed URL failed:', error);
      return false;
    }
  }
}

// Singleton instance
export const apiClient = new ApiClient();
export default apiClient;
